import { useState } from 'react';
import PropTypes from 'prop-types';
import { alpha, styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faTimes } from '@fortawesome/free-solid-svg-icons';

import { removeSyllabus } from '../../../functions/Api';
import { formatTime } from '../../../functions/Utils';

const downloadLink = (classNumber, strm) => {
  return '/syllabus/' + strm + '/' + classNumber;
};

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

// change sortingEnabled flag to allow sorting on any of the columns - only single string items will support sorting for now
const headCells = [
  {
    id: 'course',
    numeric: false,
    disablePadding: false,
    label: 'Course',
    sortingEnabled: false,
  },
  {
    id: 'description',
    numeric: false,
    disablePadding: false,
    label: 'Title',
    sortingEnabled: false,
  },
  {
    id: 'classNumber',
    numeric: false,
    disablePadding: false,
    label: 'Class #',
    sortingEnabled: false,
  },
  {
    id: 'days',
    numeric: false,
    disablePadding: false,
    label: 'Days',
    sortingEnabled: false,
  },
  {
    id: 'startTime',
    numeric: false,
    disablePadding: false,
    label: 'Start',
    sortingEnabled: false,
  },
  {
    id: 'endTime',
    numeric: false,
    disablePadding: false,
    label: 'End',
    sortingEnabled: false,
  },
  {
    id: 'location',
    numeric: false,
    disablePadding: false,
    label: 'Location',
    sortingEnabled: false,
  },
  {
    id: 'facilityId',
    numeric: false,
    disablePadding: false,
    label: 'Facility Id',
    sortingEnabled: false,
  },
  {
    id: 'instructors',
    numeric: false,
    disablePadding: false,
    label: 'Instructor(s)',
    sortingEnabled: false,
  },
  {
    id: 'hasSyllabus',
    numeric: false,
    disablePadding: false,
    label: '',
    sortingEnabled: false,
  },
  {
    id: 'hasSyllabus2',
    numeric: false,
    disablePadding: false,
    label: '',
    sortingEnabled: false,
  },
];

const StyledTableSortLabel = styled(TableSortLabel)(({ theme }) => ({
  '& .MuiTableSortLabel-icon': {
    opacity: 0.5,
  },
}));

const VisuallyHidden = styled('span')({
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: 1,
  margin: -1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  top: 20,
  width: 1,
});

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortingEnabled && (
              <StyledTableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : 'asc'}
                onClick={createSortHandler(headCell.id)}
                className="text-nowrap"
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <VisuallyHidden>
                    {order === 'desc'
                      ? 'sorted descending'
                      : 'sorted ascending'}
                  </VisuallyHidden>
                ) : null}
              </StyledTableSortLabel>
            )}
            {!headCell.sortingEnabled && <>{headCell.label}</>}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(1),
  ...(theme.palette.mode === 'light' && {
    '&.highlight': {
      color: theme.palette.secondary.main,
      backgroundColor: alpha(theme.palette.secondary.light, 0.85),
    },
  }),
  ...(theme.palette.mode === 'dark' && {
    '&.highlight': {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.secondary.dark,
    },
  }),
}));

const TitleTypography = styled(Typography)({
  flex: '1 1 100%',
});

const EnhancedTableToolbar = (props) => {
  const { numSelected } = props;

  return (
    <StyledToolbar className={numSelected > 0 ? 'highlight' : ''}>
      {numSelected > 0 ? (
        <TitleTypography color="inherit" variant="subtitle1" component="div">
          {numSelected} selected
        </TitleTypography>
      ) : (
        <TitleTypography variant="h6" id="tableTitle" component="div">
          Nutrition
        </TitleTypography>
      )}

      {numSelected > 0 ? (
        <Tooltip title="Delete">
          <IconButton aria-label="delete" size="large">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Filter list">
          <IconButton aria-label="filter list" size="large">
            <FilterListIcon />
          </IconButton>
        </Tooltip>
      )}
    </StyledToolbar>
  );
};

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(2),
}));

const StyledTable = styled(Table)({
  minWidth: 750,
});

export default function EnhancedTable(props) {
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');

  const removeLink = async (e, classNumber, strm) => {
    e.stopPropagation();

    props.setUploading(true);
    props.setUploadResults([]);
    if (window.confirm('Are you sure you want to remove this syllabus?')) {
      let removeResult = await removeSyllabus(classNumber, strm, props.viewAs);
      props.setUploadResults((results) => {
        return results.concat(removeResult);
      });
      props.updateSyllabusStatus(classNumber, strm);
    }
    props.setUploading(false);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      props.setSelected(props.rows);
      return;
    }
    props.setSelected([]);
  };

  const handleClick = (event, row) => {
    const selectedIndex = props.selected.findIndex(
      (element) => element.classNumber === row.classNumber
    );
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(props.selected, row);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(props.selected.slice(1));
    } else if (selectedIndex === props.selected.length - 1) {
      newSelected = newSelected.concat(props.selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        props.selected.slice(0, selectedIndex),
        props.selected.slice(selectedIndex + 1)
      );
    }

    props.setSelected(newSelected);
  };

  const isSelected = (classNumber) =>
    props.selected.findIndex(
      (element) => element.classNumber === classNumber
    ) !== -1;

  return (
    <div className="w-100 mb-2">
      <StyledPaper>
        {/*<EnhancedTableToolbar numSelected={selected.length} />*/}
        <TableContainer>
          <StyledTable
            aria-labelledby="tableTitle"
            size={'small'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              numSelected={props.selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={props.rows.length}
            />
            <TableBody>
              {stableSort(props.rows, getComparator(order, orderBy)).map(
                (row, index) => {
                  const isItemSelected = isSelected(row.classNumber);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.classNumber}
                      selected={isItemSelected}
                    >
                      <TableCell align="left">{row.course}</TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        {row.description}
                      </TableCell>
                      <TableCell align="left">{row.classNumber}</TableCell>
                      {(() => {
                        // Create arrays to hold meeting data
                        const days = [];
                        const startTimes = [];
                        const endTimes = [];
                        const facilityIds = [];
                        const instructors = [];

                        // Map through meetings once
                        row.meetings?.forEach((meeting, index) => {
                          // Days
                          if (meeting.days !== '') {
                            days.push(
                              <div key={index} className="text-nowrap">
                                {meeting.days}
                              </div>
                            );
                          } else {
                            days.push(<div key={index}>&nbsp;</div>);
                          }

                          // Start Time
                          if (meeting.startTime) {
                            startTimes.push(
                              <div key={index} className="text-nowrap">
                                {formatTime(meeting.startTime)}
                              </div>
                            );
                          } else {
                            startTimes.push(<div key={index}>&nbsp;</div>);
                          }

                          // End Time
                          if (meeting.endTime) {
                            endTimes.push(
                              <div key={index} className="text-nowrap">
                                {formatTime(meeting.endTime)}
                              </div>
                            );
                          } else {
                            endTimes.push(<div key={index}>&nbsp;</div>);
                          }

                          // Facility ID
                          facilityIds.push(
                            <div key={index} className="text-nowrap">
                              {meeting.facilityId.includes('ASUSYNC')
                                ? 'ASU Sync'
                                : meeting.facilityId}
                            </div>
                          );

                          // Instructors
                          if (
                            meeting.instructors &&
                            meeting.instructors.length > 0
                          ) {
                            meeting.instructors.map(
                              (instructor) =>
                                !instructors.includes(
                                  instructor.firstName +
                                    ' ' +
                                    instructor.lastName
                                ) &&
                                instructors.push(
                                  instructor.firstName +
                                    ' ' +
                                    instructor.lastName
                                )
                            );
                          }
                        });

                        // Return the TableCells with the prepared elements
                        return (
                          <>
                            <TableCell>{days}</TableCell>
                            <TableCell>{startTimes}</TableCell>
                            <TableCell>{endTimes}</TableCell>
                            <TableCell>
                              {row.locationDescription === 'Dwnwn Phx'
                                ? 'Dtphx'
                                : row.locationDescription === 'Polytech'
                                  ? 'Poly'
                                  : row.locationDescription}
                            </TableCell>
                            <TableCell>{facilityIds}</TableCell>
                            <TableCell>
                              {instructors.map((instructor, index, array) => (
                                <div key={index}>
                                  {instructor}
                                  {index < array.length - 1 ? ', ' : ''}
                                </div>
                              ))}
                            </TableCell>
                          </>
                        );
                      })()}
                      <TableCell align="left">
                        {row.hasSyllabus ? (
                          <>
                            <a
                              href={downloadLink(row.classNumber, row.strm)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-nowrap d-flex gap-1"
                            >
                              <FontAwesomeIcon icon={faFile} size="lg" />
                              <span>View PDF</span>
                            </a>
                          </>
                        ) : (
                          ''
                        )}
                      </TableCell>
                      <TableCell align="left">
                        {row.hasSyllabus ? (
                          <>
                            <a
                              onClick={(e) =>
                                removeLink(e, row.classNumber, row.strm)
                              }
                              href="#"
                              className="text-nowrap d-flex gap-1"
                            >
                              <FontAwesomeIcon icon={faTimes} size="lg" />
                              <span>Remove PDF</span>
                            </a>
                          </>
                        ) : (
                          ''
                        )}
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
            </TableBody>
          </StyledTable>
        </TableContainer>
      </StyledPaper>
    </div>
  );
}
